import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { formatMoney } from "@/lib/prefs";
import { Trash2, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  { value: "dropped", label: "Dropped" },
  { value: "burnt", label: "Burnt / over-cooked" },
  { value: "sent_back", label: "Sent back by guest" },
  { value: "expired", label: "Expired / spoiled" },
  { value: "other", label: "Other" },
];

// One-tap waste logger for a recipe. Records N wasted plates as a
// deduction (kind="waste"), deducting the ingredients from inventory
// and rolling into the Sunday Weekly Cost Digest.
export default function WasteDialog({ recipe, onLogged }) {
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [reason, setReason] = useState("dropped");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/deductions", {
        kind: "waste",
        recipe_id: recipe.id,
        quantity: Number(qty) || 1,
        reason,
        notes: notes || undefined,
      });
      setResult(data);
      onLogged?.(data);
      toast.success(`Logged ${data.quantity} × ${data.recipe_name} as waste`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setResult(null);
    setQty(1);
    setReason("dropped");
    setNotes("");
  };

  return (
    <>
      <Button
        variant="outline"
        className="gap-2 text-destructive hover:text-destructive"
        onClick={() => setOpen(true)}
        data-testid="recipe-waste-btn"
      >
        <Trash2 className="w-4 h-4" /> Log waste
      </Button>
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
        <DialogContent data-testid="waste-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {result ? "Waste logged" : `Log waste • ${recipe.name}`}
            </DialogTitle>
          </DialogHeader>

          {!result ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Plates dropped, burnt, sent back or otherwise binned. Deducts inventory and shows in Sunday's Cost Digest.
              </p>
              <div>
                <Label>Plates wasted</Label>
                <Input
                  type="number"
                  min="1"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  data-testid="waste-qty"
                  autoFocus
                />
              </div>
              <div>
                <Label>Reason</Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="waste-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Table 4 sent it back: over-salted"
                  className="min-h-[64px]"
                  data-testid="waste-notes"
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={close}>Cancel</Button>
                <Button
                  onClick={submit}
                  disabled={saving}
                  data-testid="waste-submit"
                  className="gap-2 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Log waste
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Plates wasted" value={`× ${result.quantity}`} />
                <Stat label="Food cost lost" value={formatMoney(result.food_cost)} highlight />
              </div>
              {result.inventory_shortfalls?.length > 0 && (
                <div className="rounded gold-hair border bg-primary/5 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 gold" />
                    <div className="label-eyebrow gold text-[0.6rem]">Inventory shortfalls</div>
                  </div>
                  <ul className="text-xs text-muted-foreground space-y-0.5">
                    {result.inventory_shortfalls.map((s, i) => (
                      <li key={i}>
                        <span className="text-foreground">{s.name}</span>: needed {s.needed.toFixed(2)} {s.unit}, on-hand {s.on_hand.toFixed(2)} {s.unit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Rolled into this week's Cost Digest. Reply to the Sunday email or open <span className="text-foreground">/billing</span> to preview it.
              </p>
              <DialogFooter>
                <Button onClick={close} data-testid="waste-done">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded border border-border p-3 ${highlight ? "bg-destructive/10" : "bg-card"}`}>
      <div className="label-eyebrow text-[0.55rem]">{label}</div>
      <div className={`font-display text-xl tabular mt-1 ${highlight ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}
